'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useEvent, useUnit } from 'effector-react';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { addExpandedMenuItemIds } from '@nx-frontend/shared/store/feature-menu-tree';
import { $workspaceTags, createWorkspaceTagEvent } from '@nx-frontend/shared/store/tags';
import { webnotesThemeColors, webnotesThemeStyle } from '@nx-frontend/shared/themes/webnotes';
import { PopupType } from '@nx-frontend/shared/types';

export const useCreateTag = () => {
  const t = useTranslations();
  const [openModal, createWorkspaceTag] = useEvent([openModalEvent, createWorkspaceTagEvent]);
  const [workspaceTags] = useUnit([$workspaceTags]);
  const router = useRouter();
  const { toast } = useToast();

  /**
   * Shows create tag modal
   * @param workspaceId
   * @params openNotesByTag
   */
  return useCallback(
    ({ workspaceId, openNotesByTag }: { workspaceId: string; openNotesByTag?: boolean }) => {
      const modalTitle = t('webnotes.webnotes-create_tag');
      const folderDefaultTitle = t('webnotes.webnotes-new_tag');

      openModal({
        title: modalTitle,
        content: PopupType.RENAME,
        showCloseButton: true,
        closeButtonSize: 18,
        payload: {
          type: 'tag',
          value: folderDefaultTitle,
          payloadType: PopupType.RENAME,
          themeColors: webnotesThemeColors,
          themeStyle: webnotesThemeStyle,
          onApply: async (value: string) => {
            if (workspaceTags.tags.some((tag) => tag.name === value)) {
              toast({ title: t('webnotes.webnotes-such_tag_already_exists') });
              return;
            }
            createWorkspaceTag({
              workspaceId,
              tagName: value,
            });
            if (openNotesByTag) {
              addExpandedMenuItemIds([]);
              router.push('/space/' + workspaceId + '/tag/' + encodeURIComponent(value));
            }
          },
        },
      });
    },
    [createWorkspaceTag, openModal, router, t, toast, workspaceTags.tags]
  );
};
